import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Sun, Moon, Monitor, Check } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { getThemePref, setThemePref } from "@/lib/theme";

// Theme picker: an explicit menu of the three preferences — light, dark, and
// system (follow the OS). A single-button *cycle* was confusing: two positions
// (dark, and system while the OS is dark) render identically, so the standalone
// dark step looked missing. A menu shows all three at once with the active one
// checked, so no mode can appear absent regardless of OS. Single source of
// preference state = lib/theme.js. The trigger keeps the 44px touch target,
// keyboard focus, and reflects the current preference's icon.
const OPTIONS = [
  { pref: "light", Icon: Sun, key: "theme.light", fallback: "Clair" },
  { pref: "dark", Icon: Moon, key: "theme.dark", fallback: "Sombre" },
  { pref: "system", Icon: Monitor, key: "theme.system", fallback: "Système" },
];

const ThemeToggle = ({ className = "" }) => {
  const { t } = useTranslation();
  const [pref, setPref] = useState(getThemePref());
  const current = OPTIONS.find((o) => o.pref === pref) || OPTIONS[0];
  const TriggerIcon = current.Icon;

  const choose = (next) => setPref(setThemePref(next));

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={t("theme.label", "Thème")}
          title={`${t("theme.label", "Thème")} : ${t(
            current.key,
            current.fallback
          )}`}
          data-testid="theme-toggle"
          data-theme-pref={pref}
          className={`inline-flex items-center justify-center w-10 h-10 rounded-lg text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 ${className}`}
        >
          <TriggerIcon className="w-5 h-5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44 bg-card border-white/10">
        {OPTIONS.map(({ pref: p, Icon, key, fallback }) => (
          <DropdownMenuItem
            key={p}
            onClick={() => choose(p)}
            className="cursor-pointer flex items-center justify-between"
            data-testid={`theme-option-${p}`}
          >
            <span className="flex items-center gap-2">
              <Icon className="w-4 h-4 shrink-0" />
              <span>{t(key, fallback)}</span>
            </span>
            {pref === p && <Check className="w-4 h-4 text-primary" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ThemeToggle;
